import * as dotProp from 'dot-prop-immutable';
import uniq from 'lodash/uniq';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import moment from 'moment';
import { DECISION_NODE_SEND_MACRO_ACTIONS, USPS_STORAGE_THRESHOLD } from '../constants/decisionTree';
import { formatListWithCommas, maskEmailAddress, removeLastTagFromPath, splitTagPath } from './formatUtil';
import { isDayLessThanTresholdAgo } from './util';
import { ConfigurableNodeError, NegativeDiscount, UnexpectedOrderingSystem, UnsupportedCarrier } from './error';
import { SHOPIFY_FINANCIAL_STATUSES } from '../constants/shopifyCancelAndRefunds';
import { formatOrderDateToLocalTime, moneyRegex } from '../util';
import { orderSystemMappings } from './decisionTreeOrderSystemMap';
import toNumber from 'lodash/toNumber';
import isBoolean from 'lodash/isBoolean';
import { GENERIC_ERROR_MESSAGE } from '../components/DecisionTree/NodeGroups/DTConfigurableNode/Common/ConditionProcessor/ConditionProcessor';

const regEx = /{{((\w\.?)+)}}/g;
export const PARTNERS = {
  RBI: 'rbi',
  HUM: 'hum',
};

// values of these properties need to be shown in the human-readable date format
export const dateSelectors = ['createdAt'];

// map of selector to desired label
export const alternateLabelsForSelectors = {
  trackingNumberAsLink: 'TRACKING NUMBER',
};

export const getBreadcrumbTitleOfNode = (decisionNode) => {
  const { breadcrumbTitle, action } = decisionNode;
  // check breadcrumb title first
  if (breadcrumbTitle) {
    return breadcrumbTitle;
  }
  // next see if there is a default value based on the type
  if (DECISION_NODE_SEND_MACRO_ACTIONS.indexOf(action) > -1) {
    return 'Macro';
  }

  return undefined;
};

export const getStateFromPressingBack = (macroWizardState) => {
  /**
   * Pressing back should remove the last items at the end of the path
   * So the ordering should be as follows:
   *   1. No match page
   *   2. Selected Macro
   *   3. Tag path
   * Decision tree path handled in thunk returnToPreviousMacroWizardPath
   */
  if (macroWizardState.noMatchPageActivated) {
    return dotProp.set(macroWizardState, 'noMatchPageActivated', false);
  } else if (macroWizardState.selectedMacro) {
    return dotProp.set(macroWizardState, 'selectedMacro', null);
  } else if (macroWizardState.tagPath) {
    const newPath = removeLastTagFromPath(macroWizardState.tagPath);

    return dotProp.set(macroWizardState, 'tagPath', newPath);
  }

  return macroWizardState;
};

export const getShopifyShipmentStatusOffOrderShipment = (orderShipment) => {
  const { tracking_company } = orderShipment;
  if (tracking_company) {
    return getShippoCarrierFromShopifyCarrierWithoutError(tracking_company);
  }

  return false;
};

export const isCarrierSupportedInShippo = (orderShipment, orderingSystem) => {
  switch (orderingSystem) {
    case 'shopify':
      return !!getShopifyShipmentStatusOffOrderShipment(orderShipment);
    case 'bigCommerce':
      const bigCommerce_carrier_code = getCarrierCodeOfShipment(orderShipment, orderingSystem);

      return !!bigCommerce_carrier_code;
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
};

export const getShippoCarrierFromShopifyCarrierWithoutError = (shopifyCarrier) => {
  if (shopifyCarrier === 'DHL Express') {
    return 'dhl_express';
  } else if (shopifyCarrier === 'DHL eCommerce') {
    return 'dhl_ecommerce';
  } else if (shopifyCarrier && shopifyCarrier.startsWith('FedEx')) {
    return 'fedex';
  } else if (shopifyCarrier === 'UPS') {
    return 'ups';
  } else if (shopifyCarrier === 'USPS') {
    return 'usps';
  } else if (shopifyCarrier === 'Canada Post') {
    return 'canada_post';
  } else if (shopifyCarrier === 'OnTrac') {
    return 'ontrac';
  }

  return null;
};

export const getShippoCarrierNameForBigCommerce = (bigCommerceCarrier = '') => {
  const name = bigCommerceCarrier.toLowerCase();

  if (name.includes('ups')) {
    return 'ups';
  } else if (name.includes('fedex')) {
    return 'fedex';
  } else if (name.includes('usps')) {
    return 'usps';
  } else if (name.includes('dhl')) {
    return 'dhl_ecommerce';
  }
  throw new UnsupportedCarrier(name);
};

export const isExpiredMacro = (macro) => {
  if (!macro || !macro.expirationDate) {
    return false;
  }
  const macroExpirationDate = macro.expirationDate;
  const momentNow = moment();
  const momentExpiration = moment(macroExpirationDate);

  return momentExpiration.isValid() && momentNow.isAfter(momentExpiration);
};

export const checkIfMacroIsNew = (macro) => {
  const now = moment();
  const time_existed = now - moment(macro.createdAt);
  const two_weeks_ago = now - now.clone().subtract(14, 'days').startOf('day');

  return time_existed < two_weeks_ago;
};

export const getIconNameForMacro = (macro) => {
  let iconName;

  if (checkIfMacroIsNew(macro)) {
    iconName = 'macro-icon-new';
  } else {
    iconName = 'macro-icon-normal';
  }

  return iconName;
};

export const sortMacrosByUsage = (a, b) => {
  const now = moment();
  // January 30th is the date when we started to collect the data needed to calculate usageCount
  const jan30 = moment(new Date('January 30, 2019'));
  const a_created_at = moment(a.createdAt);
  const a_use_per_millisecond = a.usageCount / Math.min(now - a_created_at, now - jan30);
  const b_created_at = moment(b.createdAt);
  const b_use_per_millisecond = b.usageCount / Math.min(now - b_created_at, now - jan30);

  return b_use_per_millisecond - a_use_per_millisecond;
};

export function getNameFromItem(item, orderingSystem) {
  // TODO: Add default case
  // eslint-disable-next-line
  switch (orderingSystem) {
    case 'common':
      return item.itemName;
    case 'shopify':
    case 'custom_rbi':
    case 'salesforce':
    case 'bigCommerce':
      return item.name;
    case 'revolve':
      return item.description;
  }
}

export const replaceTemplateTextForLineItems = (text, lineItems, orderShipment, orderingSystem) => {
  // first level is non-ordering system specific
  const out = text.replace(regEx, (match, path) => {
    // TODO: Add default case
    // eslint-disable-next-line
    switch (path) {
      case 'orderShipment.carrier':
        return getCarrierCodeOfShipment(orderShipment, orderingSystem);
      case 'orderShipment.trackingNumber':
        return getTrackingNumberOfShipment(orderShipment, orderingSystem);
    }

    // default to the template
    return match;
  });

  // if we made replacements then use that
  if (out !== text) {
    return out;
  }

  // second level uses specific functions for shopify
  // eventually should migrate these to the switch statement above
  switch (orderingSystem) {
    case 'shopify':
      return replaceTemplateTextForShopifyLineItems(text, lineItems, orderShipment);
    case 'bigCommerce':
      return replaceTemplateTextForBigCommerceLineItems(text, lineItems, orderShipment);
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
};

export const replaceTemplateTextForShopifyLineItems = (text, lineItems, orderShipment) => {
  return text.replace(regEx, (match, path) => {
    // TODO: Add default case
    // eslint-disable-next-line
    switch (path) {
      case 'lineItems.productNames':
        let productNames = lineItems.map((item) => item.name);
        productNames = uniq(productNames);

        return formatListWithCommas(productNames);
      case 'orderShipment.trackingLink':
        return orderShipment.tracking_url;
      case 'orderShipment.created_at':
        const date = get(orderShipment, 'created_at');

        return date ? formatOrderDateToLocalTime(date, 'shopify') : '';
    }

    // default to the template
    return match;
  });
};

export const replaceTemplateTextForBigCommerceLineItems = (text, lineItems = [], orderShipment) => {
  return text.replace(regEx, (match, path) => {
    // TODO: Add default case
    // eslint-disable-next-line
    switch (path) {
      case 'lineItems.productNames':
        let productNames = lineItems.map((item) => item.name);
        productNames = uniq(productNames);

        return formatListWithCommas(productNames);
      case 'orderShipment.trackingLink':
        return orderShipment.tracking_link;
      case 'orderShipment.created_at':
        const date = get(orderShipment, 'date_created');

        return date ? formatOrderDateToLocalTime(date, 'bigCommerce') : '';
    }

    // default to the template
    return match;
  });
};

export const getReadableStatusFromShippoStatus = (shippoStatus) => {
  switch (shippoStatus) {
    case 'TRANSIT':
      return 'in transit';
    case 'PRE_TRANSIT':
      return 'pre-transit';
    case 'DELIVERED':
      return 'delivered';
    default:
      // should never happen but in case it does, show unknown
      return 'unknown';
  }
};

export const getReadableStatusFromShopifyStatus = (shopifyStatus) => {
  switch (shopifyStatus) {
    case 'attempted_delivery':
    case 'in_transit':
    case 'out_for_delivery':
    case 'shipped':
      return 'in transit';
    case 'label_printed':
    case 'label_purchased':
      return 'pre-transit';
    case 'delivered':
      return 'delivered';
    case 'ready_for_pickup':
      return 'ready for pickup';
    default:
      // should never happen but in case it does, show unknown
      return 'unknown';
  }
};

export const checkIfTrackingInfoTooOldForCarrier = (carrier, date) => {
  // older usps deliverys show as being pre-transit whereas in reality they are unavailable
  return carrier === 'usps' && !isDayLessThanTresholdAgo(date, USPS_STORAGE_THRESHOLD);
};

export const getOrderShipmentFromOrderShipmentsList = (orderingSystem, orderShipments, shipmentId) => {
  switch (orderingSystem) {
    case 'shopify':
      return orderShipments.find(
        (shipment) => shipment.id === shipmentId || String(shipment.shipmentId) === String(shipmentId)
      );
    case 'bigCommerce':
      // orderShipments = fulfillments
      return orderShipments.find((shipment) => shipment.id === shipmentId);
    case 'revolve':
      return orderShipments.find((shipment) => shipment.id === shipmentId);
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
};

export const getNumberOfTrackings = (orderShipment, orderingSystem) => {
  switch (orderingSystem) {
    case 'shopify':
      return orderShipment.tracking_urls && orderShipment.tracking_urls.length;
    case 'bigCommerce':
      return orderShipment.tracking_link && orderShipment.tracking_link.length;
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
};

export const getItemName = (item, orderingSystem) => {
  switch (orderingSystem) {
    case 'common':
      return item.itemName;
    case 'shopify':
      return item.name;
    case 'revolve':
      return item.description;
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
};

/**
 * Returns ineligiblity reason for a refund on an item.
 * Currently, this is chegg specific.
 * This value can be undefined for other partners/ brands
 * @param {*} response
 * @param {*} itemId
 * @returns
 */
export const getItemIneligiblityReason = (response, itemId) => {
  return response[itemId]?.ineligibilityReason;
};

export const getSelectedOrderNumber = (order, orderingSystem) => {
  switch (orderingSystem) {
    case 'shopify':
      return order.name;
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
};

export const getCarrierCodeOfShipment = (orderShipment, orderingSystem) => {
  switch (orderingSystem) {
    case 'common':
      return get(orderShipment, 'carrier');
    case 'shopify':
      const { tracking_company } = orderShipment;
      // needed in lowercase format in parent function
      if (tracking_company) {
        return tracking_company.toLowerCase();
      }

      return '';
    case 'bigCommerce':
      const { shipping_provider, tracking_carrier } = orderShipment;
      // needed in lowercase format in parent function
      if (shipping_provider) {
        return shipping_provider.toLowerCase();
      } else if (tracking_carrier) {
        return tracking_carrier.toLowerCase();
      }

      return '';
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
};

export const getTrackingNumberOfShipment = (orderShipment, orderingSystem) => {
  switch (orderingSystem) {
    case 'common':
      return get(orderShipment, 'trackingNumber');
    case 'shopify':
      return get(orderShipment, 'tracking_number');
    case 'bigCommerce':
      return get(orderShipment, 'tracking_number');
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
};

export const mapShopifyShipmentStatusToDecisionTreeOptions = (shipmentStatus) => {
  switch (shipmentStatus) {
    case 'attempted_delivery':
    case 'in_transit':
    case 'out_for_delivery':
      return 'TRANSIT';
    case 'label_printed':
    case 'label_purchased':
      return 'PRE_TRANSIT';
    case 'delivered':
      return 'DELIVERED';
    default:
      return 'UNHANDLED_OPTION';
  }
};

export const getTrackingStatus = (orderShipment, shippoInfo, orderingSystem, carrierSupportedInShippo) => {
  switch (orderingSystem) {
    case 'bigCommerce':
      return get(shippoInfo, 'tracking_status.status');
    case 'shopify':
      const statusFromShippo = get(shippoInfo, 'tracking_status.status');
      // check that URL is supported in shippo AND that we have a shippo status
      if (carrierSupportedInShippo && statusFromShippo) {
        return statusFromShippo;
      }
      const shopifyStatus = orderShipment.shipment_status;

      return mapShopifyShipmentStatusToDecisionTreeOptions(shopifyStatus);

    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
};

export const getLastTrackingUpdateDate = (orderShipment, shippoInfo, orderingSystem) => {
  switch (orderingSystem) {
    case 'shopify':
      return orderShipment.updated_at;
    case 'bigCommerce':
      return orderShipment.date_created;
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
};

export function getFullNameFromOrder(order, orderingSystem) {
  // TODO: Add default case
  // eslint-disable-next-line
  switch (orderingSystem) {
    case 'common':
      const firstName = get(order, 'customer.firstName', '');
      const lastName = get(order, 'customer.lastName', '');
      if (firstName && lastName) {
        return `${firstName} ${lastName}`;
      } else if (order.customerEmail) {
        // some systems do not have customer name available
        // on orderList query
        return maskEmailAddress(order.customerEmail);
      }

      return firstName;
    case 'shopify':
      return order.billing_address && order.billing_address.name;
    case 'custom_rbi':
      return order.userDetails && order.userDetails.name;
    case 'salesforce':
      return order.user && order.user.Name;
    case 'bigCommerce':
      return order.billing_address && `${order.billing_address.first_name} ${order.billing_address.last_name}`;
    case 'revolve':
      return order.useremail;
  }
}

export function getMappedOrderingSystemPropertyName(field, orderingSystem) {
  const mappedProperty = get(orderSystemMappings, [orderingSystem, field], field);

  return isString(mappedProperty) ? mappedProperty : field;
}

export function getItemId(item, orderingSystem) {
  // TODO: Add default case
  // eslint-disable-next-line
  switch (orderingSystem) {
    case 'common':
      return item.itemId;
    case 'shopify':
      return item.id?.toString() ?? item.lineItemKey;
    case 'custom_rbi':
      return item.sanityId;
    case 'salesforce':
      return item.Id;
    case 'bigCommerce':
      return item.id;
    case 'revolve':
      return item.id;
  }
}

export function getTotalPriceOfItems(selectedLineItems, orderingSystem) {
  let value;
  switch (orderingSystem) {
    case 'common':
      value = selectedLineItems.reduce((total, item) => {
        return parseFloat(item.paidItemTotalPrice) + total;
      }, 0);

      return value.toFixed(2);
    case 'shopify':
      value = selectedLineItems.reduce((total, item) => {
        return parseFloat(item.price) * parseFloat(item.quantity) + total;
      }, 0);

      return value.toFixed(2);
    default:
      return null;
  }
}

export function getCarrier(item, orderingSystem) {
  switch (orderingSystem) {
    case 'common':
      // Items almost always have one shipment
      return get(item, 'shipments[0].carrier');
    case 'shopify':
      const fulfillmentData = item['fulfillmentData'] || {};
      if (isEmpty(fulfillmentData)) {
        return '';
      }

      return get(fulfillmentData, 'tracking_company', '');
    default:
      return '';
  }
}

export function getTrackingNumber(item, orderingSystem) {
  switch (orderingSystem) {
    case 'common':
      // Items almost always have one shipment
      return get(item, 'shipments[0].trackingNumber');
    case 'shopify':
      const fulfillmentData = item['fulfillmentData'] || {};
      if (isEmpty(fulfillmentData)) {
        return [];
      }

      return get(fulfillmentData, 'tracking_urls', []);
    default:
      return [];
  }
}

export function getTotalPriceOfSubscriptionItems(selectedSubscriptionList, orderingSystem) {
  switch (orderingSystem) {
    case 'shopify':
      const value = selectedSubscriptionList.reduce((total, sub) => {
        return parseFloat(sub.price) * parseFloat(sub.quantity) + total;
      }, 0);

      return value.toFixed(2);
    default:
      return null;
  }
}

export function isOrderRefunded(order, orderingSystem) {
  switch (orderingSystem) {
    case 'shopify':
      return order.financial_status === SHOPIFY_FINANCIAL_STATUSES.REFUNDED;
    case 'bigCommerce':
      return order.is_refunded;
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
}

export function isOrderPartiallyOrFullyRefunded(order, orderingSystem) {
  switch (orderingSystem) {
    case 'shopify':
      return [SHOPIFY_FINANCIAL_STATUSES.REFUNDED, SHOPIFY_FINANCIAL_STATUSES.PARTIALLY_REFUNDED].includes(
        order.financial_status
      );
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
}

export function isOrderShippingRefunded(order, orderingSystem) {
  switch (orderingSystem) {
    case 'shopify':
      const refunds = get(order, 'refunds', []);
      const orderAdjustments = refunds.flatMap((refund) => get(refund, 'order_adjustments', []));
      const orderShippingRefund = orderAdjustments.filter(
        (adjustment) => adjustment.kind === SHOPIFY_FINANCIAL_STATUSES.SHIPPING_REFUND
      );

      return !isEmpty(orderShippingRefund);
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
}

export function hasShippingCharges(order, orderingSystem) {
  switch (orderingSystem) {
    case 'shopify':
      const shippingLines = get(order, 'shipping_lines', []);
      const orderFinancialStatusIsPending = order.financial_status === SHOPIFY_FINANCIAL_STATUSES.PENDING;
      const shippingChargesExist = shippingLines.some((shippingLine) => {
        const charge = get(shippingLine, 'discounted_price_set.shop_money.amount');

        return String(charge) > 0;
      });

      return !isEmpty(shippingLines) && shippingChargesExist && !orderFinancialStatusIsPending;
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
}

export function isOrderCanceled(order, orderingSystem) {
  switch (orderingSystem) {
    case 'shopify':
      return !!order.cancelled_at;
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
}

export const processForRefundCalculation = (items, orderingSystem) => {
  switch (orderingSystem) {
    case 'shopify':
      return items.map((item) => ({
        id: String(item.id),
        quantity: item.quantity,
      }));
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
};

// Get the value from a Shopify order or item based off the field name
// this uses the lookup table as defined in getMappedValueFromOrderOrItem
export const getMappedValueFromOrderOrItem = (orderOrItem, field, orderingSystem) => {
  if (isString(field)) {
    const mapping = get(orderSystemMappings, [orderingSystem, field], field);
    // if we the mapping is a function, call it with the orderOrItem and the orderingSystem
    if (typeof mapping === 'function') {
      return mapping(orderOrItem, orderingSystem);
    } else if (isString(mapping)) {
      // return the value of that field
      return get(orderOrItem, mapping);
    }
  } else {
    if (field.type === 'array') {
      try {
        const lastIndex = field.dataPath.lastIndexOf('.');
        const array = field.dataPath.slice(0, lastIndex);
        const child = field.dataPath.slice(lastIndex + 1);
        const arrayInfo = get(orderOrItem, array, undefined);

        return isUndefined(get(orderOrItem, `${array}[0].${child}`, undefined))
          ? undefined
          : arrayInfo.map((value) => value[child]);
      } catch (e) {
        return get(orderOrItem, field, undefined);
      }
    }
  }

  return get(orderOrItem, field);
};

export const orderItemsWereCanceled = (order) => {
  const hasCanceledItems =
    order.items &&
    order.items.some((item) => {
      return parseFloat(item['qty_refunded']) > 0 && parseFloat(item['qty_shipped']) === 0;
    });

  return hasCanceledItems || order.status === 'canceled';
};

export const getCanceledItemsInOrder = (order) => {
  if (order.status === 'canceled') {
    return order.items;
  }
  const canceledItems = [];
  order.items.forEach((item) => {
    if (parseFloat(item['qty_refunded']) > 0 && parseFloat(item['qty_shipped']) === 0) {
      canceledItems.push(item);
    }
  });

  return canceledItems;
};

export const countItems = (items) => {
  let count = 0;
  items.forEach((item) => {
    count += parseInt(item['qty_ordered']);
  });

  return count;
};

export const calculateItemRefundForPriceMatch = (discountPeriod, item) => {
  const discountType = discountPeriod.discountType;
  const discountAmount = discountPeriod.discount;
  const taxRate = item.tax_lines.reduce((total, record) => total + parseFloat(record.rate), 0); // adds all taxes to overall percentage - Example 10.25%
  let pretaxedFullPrice = parseFloat(item.price); // full price with no discounts (no tax)

  // item.price is the price per 1 item, need to multiply by quantity for correct total
  const quantity = parseFloat(item.quantity);
  if (item.quantity && typeof quantity === 'number') {
    pretaxedFullPrice = pretaxedFullPrice * quantity;
  }

  // determine what the customer has already paid, including taxes
  let alreadyPaidTotal;
  // non-previously discounted orders do not have pre_tax_price
  if (item.pre_tax_price) {
    alreadyPaidTotal = parseFloat(item.pre_tax_price) + parseFloat(item.pre_tax_price) * taxRate; // amount customer actually paid, including taxes
  } else {
    alreadyPaidTotal = pretaxedFullPrice + pretaxedFullPrice * taxRate;
  }

  let newDiscountedPrice;
  // discountAmount will always be a full number
  // for example, 40% will be 40, not .4
  if (discountType === 'PERCENTAGE') {
    newDiscountedPrice = (100 - discountAmount) * 0.01 * pretaxedFullPrice;
  } else if (discountType === 'DOLLAR_AMOUNT') {
    newDiscountedPrice = pretaxedFullPrice - discountAmount;
  }

  // determine the amount owed with new discount rate applied on original cost
  const newTotal = newDiscountedPrice + newDiscountedPrice * taxRate;

  const refundAmount = alreadyPaidTotal - newTotal;

  if (isNaN(refundAmount) || refundAmount < 0) {
    throw new NegativeDiscount(refundAmount);
  }

  return refundAmount;
};

export const formatOrderItemsToRefundAndCancel = (orderingSystem, selectedItems) => {
  switch (orderingSystem) {
    case 'shopify':
      return (
        selectedItems &&
        selectedItems.map((item) => ({
          id: item['id'].toString(),
          quantity: item['fulfillable_quantity'],
        }))
      );
    case 'revolve':
      return (
        selectedItems &&
        selectedItems.map((item) => ({
          id: item.shipmentID.toString(),
          quantity: item.quantity,
        }))
      );
    case 'common':
      return selectedItems;
    default:
      return [];
  }
};

export const formatOrderItemsToRefund = (orderingSystem, selectedItems) => {
  switch (orderingSystem) {
    case 'shopify':
      return (
        selectedItems &&
        selectedItems.map((item) => ({
          id: item['id'].toString(),
          quantity: item['quantity'],
        }))
      );
    case 'common':
      return selectedItems;
    default:
      return [];
  }
};

export const sortRbiPaidAndFreeItems = (selectedItemsAndSubItemsForRefund) => {
  if (!Array.isArray(selectedItemsAndSubItemsForRefund)) {
    return null;
  }

  return selectedItemsAndSubItemsForRefund.reduce(
    (result, item) => {
      if (!item.price && item.quantity && !item.points) {
        result.freeItems.push(item);
        result.freeItemsCount++;
      } else if ((item.price && item.quantity) || item.points) {
        // Items with points are still paid items, because they get refunded on RBI's backend.
        result.paidItems.push(item);
        result.paidItemsCount++;
      }

      return result;
    },
    { paidItems: [], freeItems: [], paidItemsCount: 0, freeItemsCount: 0 }
  );
};

export const isOfferAppliedToSelectedRbiOrder = (cart) => {
  if (!Array.isArray(cart)) {
    return false;
  }

  return cart.some((item) => item.type === 'OfferDiscount');
};

export const countTotalItemsPoints = (items = []) => {
  return items.reduce((total, item) => {
    const points = parseInt(item.points);
    if (!isNaN(points)) {
      total += points;
    }

    return total;
  }, 0);
};

/**
 * NOTE(apoorvyadav1|spd-11043|2024-10-15): this is how we create list of items selected by the user
 * while reaching out to an expert. we have a business requirement to add items to the internal note
 * for which the points or coupon is being issued
 * @param {*} selectedOrder - order selected for issuing points/coupons
 * @param {*} selectedItemsIds - ids of items selected for which points/coupons are issued
 * @param {*} selectedSubItems - subitems selected if only a subset of subitems are selected
 * @returns result - flattened array of items for which user will be issued points/coupons
 */
export const constructRbiCouponOrLoyaltyPointsItemsList = (selectedOrder, selectedItemsIds, selectedSubItems) => {
  /**
   * NOTE(apoorvyadav1|spd-11043|2024-10-31): we're unable to create a list of loyalty point items
   * when the customer has no orders or no order is selected by the expert.
   */
  if (isEmpty(selectedOrder)) {
    return [];
  }

  const cartItems = selectedOrder.cart;

  if (!Array.isArray(cartItems)) {
    throw new Error('cart items must be an array');
  }

  const formattedItems = [];

  const formatItemforInsert = (item) => {
    const itemCopy = { ...item };
    delete itemCopy.children;

    return itemCopy;
  };

  if (Array.isArray(selectedItemsIds) && selectedItemsIds.length) {
    selectedItemsIds
      .filter((selectedItemId) => typeof selectedItemId === 'string')
      .forEach((selectedItemId) => {
        const [rawItemId, itemIdx] = selectedItemId.split('_index_');
        const rawItem = cartItems[itemIdx];
        /**
         * NOTE(apoorvyadav1|spd-11043|2024-10-15): we validate the id from the selectedItemId
         * to the id of the item from cart to be accurate
         */
        if (rawItem.sanityId === rawItemId) {
          formattedItems.push(formatItemforInsert(rawItem));
        }
      });
  }

  /**
   * NOTE(apoorvyadav1|spd-11043|2024-11-02): When an item have subitems,
   * customer can select a subset of subitems (selectedSubItems).
   * If selectedSubItems are available, we add the sub-items for which parent item is not
   * selected, to the formattedItems.
   */
  if (!isEmpty(selectedSubItems)) {
    /**
     * NOTE(apoorvyadav1|spd-11043|2024-10-15):  To avoid duplication,
     * we select non-empty subitems and the parent item is not present in selectedItems
     */
    const filteredSubItems = Object.keys(selectedSubItems)
      .filter((itemIdKey) => !selectedItemsIds.includes(itemIdKey))
      .reduce((obj, key) => {
        if (Array.isArray(selectedSubItems[key])) {
          obj[key] = selectedSubItems[key];
        }

        return obj;
      }, {});

    for (const itemIdKey in filteredSubItems) {
      const selectedSubitemsIds = selectedSubItems[itemIdKey];
      const [rawItemId, itemIdx] = itemIdKey.split('_index_');
      const rawItem = cartItems[itemIdx];

      if (rawItem.sanityId === rawItemId) {
        selectedSubitemsIds.forEach((selectedSubItemIdIndexed) => {
          const [rawSubItemId, subItemIdx] = selectedSubItemIdIndexed.split('_index_');
          const rawSubItem = rawItem.children[subItemIdx];

          if (rawSubItem && rawSubItemId === rawSubItem.sanityId) {
            formattedItems.push(formatItemforInsert(rawSubItem));
          }
        });
      }
    }
  }

  /** NOTE(apoorvyadav1|spd-11043|2024-10-15): We're required to list all order items
   * in a generated internal note when no items or subitems are selected by the expert.
   * This can happen when customer states entire order as the reason for coupons/points.
   */
  if (isEmpty(formattedItems)) {
    for (const itemIdx in cartItems) {
      formattedItems.push(formatItemforInsert(cartItems[itemIdx]));
    }
  }

  return formattedItems;
};

export const constructRbiRefundItemsList = (selectedOrder, selectedItemsIds, selectedSubItems) => {
  if (isEmpty(selectedOrder) || !Array.isArray(selectedOrder.cart)) {
    throw new Error('Invalid params');
  }

  const result = [];

  const cartItems = selectedOrder.cart;

  if (!Array.isArray(cartItems)) {
    throw new Error('cartItems must be an array');
  }

  const selectedItemsIndexes = [];
  // adding second level items to the refundItems list
  if (Array.isArray(selectedItemsIds) && selectedItemsIds.length) {
    selectedItemsIds.forEach((itemIdIndexed) => {
      if (typeof itemIdIndexed !== 'string') {
        return;
      }

      const [rawItemId, itemIdx] = itemIdIndexed.split('_index_');
      const rawItemForRefund = Object.assign({}, cartItems[itemIdx]);
      delete rawItemForRefund.children;

      // item that's needed to be refunded must have quantity and price
      if (rawItemForRefund.sanityId === rawItemId) {
        const price = rawItemForRefund.price || 0;
        const points = rawItemForRefund.points || 0;
        result.push({
          ...rawItemForRefund,
          price,
          points,
        });
        selectedItemsIndexes.push(itemIdx);
      }
    });
  }

  // all other items must be added with quantity and price set to 0
  cartItems.forEach((rawItemNotForRefund, idx) => {
    if (!selectedItemsIndexes.includes(idx.toString())) {
      result.push({
        ...rawItemNotForRefund,
        quantity: 0,
        price: 0,
        points: 0,
      });
    }
  });

  // adding third level subitems, excluding subitems of items that have been already added
  if (!isEmpty(selectedSubItems)) {
    for (const itemIdIndexed in selectedSubItems) {
      if (!selectedItemsIds.includes(itemIdIndexed)) {
        const selectedSubitemsIds = selectedSubItems[itemIdIndexed];
        const [rawItemId, itemIdx] = itemIdIndexed.split('_index_');
        const rawItem = cartItems[itemIdx];

        if (rawItem.sanityId === rawItemId && Array.isArray(selectedSubitemsIds)) {
          selectedSubitemsIds.forEach((selectedSubItemIdIndexed) => {
            const [rawSubItemId, subItemIdx] = selectedSubItemIdIndexed.split('_index_');
            const rawSubItem = rawItem.children[subItemIdx];

            if (rawSubItem && rawSubItemId === rawSubItem.sanityId) {
              const rawSubItemCopy = Object.assign({}, rawSubItem);
              delete rawSubItemCopy.children;
              const price = rawSubItemCopy.price || 0;
              const points = rawSubItemCopy.points || 0;
              result.push({
                ...rawSubItemCopy,
                quantity: 1,
                price,
                points,
              });
            }
          });
        }
      }
    }
  }

  return result;
};

// TODO: use constructRbiRefundAmountDetails once RBI backend is ready
// currently not used as RBI not refunding delivery fees in full order refund requests
export const constructRbiRefundAmountDetails = (
  selectedOrder,
  isFullOrderRefund,
  selectedItemsAndSubItemsForRefund
) => {
  if (isEmpty(selectedOrder)) {
    throw new Error('selectedOrder is empty');
  }

  const refundAmountDetails = {};

  if (isFullOrderRefund) {
    // if full refund need to add the full refund amount breakdown
    const { subTotalCents, taxCents, delivery } = selectedOrder;

    if (!subTotalCents || isEmpty(delivery)) {
      throw new Error('Incorrect price amounts in the order');
    }

    if (subTotalCents) {
      refundAmountDetails.subTotalCents = subTotalCents;
    }

    if (taxCents) {
      refundAmountDetails.taxCents = taxCents;
    }

    const { feeCents, tipCents } = delivery;

    if (feeCents) {
      refundAmountDetails.deliveryFeeCents = feeCents;
    }

    if (tipCents) {
      refundAmountDetails.deliveryTipCents = tipCents;
    }

    // calculating total
    let totalCents = 0;

    for (const amount in refundAmountDetails) {
      totalCents += refundAmountDetails[amount];
    }

    refundAmountDetails.totalCents = totalCents;
  } else {
    // if partial refund need to calculate the refund amount of selected items (tax already included)
    if (!Array.isArray(selectedItemsAndSubItemsForRefund)) {
      throw new Error('selectedItemsAndSubItemsForRefund is not an array');
    }

    refundAmountDetails.totalCents = selectedItemsAndSubItemsForRefund.reduce((total, item) => {
      if (item.price) {
        total += item.price;
      }

      return total;
    }, 0);
  }

  return refundAmountDetails;
};

/** buildDecisionTreePath() is used to find the final edge in the decision tree
 *  will build a full path to the final edge
 *  @param {Object} node * decisionTree
 *  @param {string} fullPath
 *   @param {string} result
 *  @param {String} searchingEdge * finalEdge - (edge to buid the path to)
 *  @returns {String} * returns decision tree path 'path -- path'
 */
export const buildDecisionTreePath = (node, searchingEdge, fullPath = '', result = '') => {
  if (fullPath.includes(searchingEdge)) {
    return fullPath;
  }

  if (node.children) {
    const currentNode = node.children;

    for (const edge in currentNode) {
      const currentFullPath = !!fullPath ? fullPath + ' -- ' + edge : edge;
      result = buildDecisionTreePath(currentNode[edge], searchingEdge, currentFullPath, result);
    }
  }

  return result;
};

export const isItemCompletelyRefunded = (order, item, orderingSystem) => {
  switch (orderingSystem) {
    case 'shopify':
      const refundsExist = !!order.refunds.length;
      if (refundsExist) {
        return order.refunds.some((refund) => {
          const refundLineItems = get(refund, 'refund_line_items', []);

          return refundLineItems.some((lineItem) => {
            return lineItem.line_item_id === item.id && lineItem.quantity >= item.quantity;
          });
        });
      }

      return false;
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
};

export const isItemAtLeastPartiallyRefunded = (order, item, orderingSystem) => {
  switch (orderingSystem) {
    case 'shopify':
      if (Boolean(order.refunds.length)) {
        return order.refunds.some((refund) => {
          const refundLineItems = get(refund, 'refund_line_items', []);

          return refundLineItems.some((lineItem) => {
            return lineItem.line_item_id === item.id;
          });
        });
      }

      return false;
    default:
      throw new UnexpectedOrderingSystem(orderingSystem);
  }
};

export const treeNodeExistsAtPath = (decisionTree, decisionTreePath = '') => {
  if (isEmpty(decisionTree)) {
    return false;
  }

  const splitDecisionPath = splitTagPath(decisionTreePath);
  const currentNode = splitDecisionPath.reduce((currNode, path) => {
    if (!currNode || !currNode.children) {
      return null;
    }

    return currNode.children[path];
  }, decisionTree);

  return !!currentNode;
};

export const macroIsUsedInTree = (decisionTree, macroArticleId) => {
  if (isEmpty(decisionTree)) {
    return false;
  }

  const treeString = JSON.stringify(decisionTree);

  return treeString.includes(macroArticleId);
};

export const getTreeAtPath = (currTree, decisionTreePath) => {
  const splitDecisionPath = splitTagPath(decisionTreePath);

  for (let edge of splitDecisionPath) {
    if (!currTree || !currTree.children) {
      return currTree;
    }
    currTree = currTree.children[edge];
  }

  return currTree;
};

/**
 * Currently, Hum-specific as there are no other partner asks for a subscription field.
 * Isolate into common format if we do receive another partner's system to auto-handle.
 *
 * @TODO: Move into common format but also add partner-specific processing assuming that
 *        Hum's situation is unique where the chargeback amount is embedded into the title.
 */
export const getSubscriptionField = (selectedSubscription, fieldName) => {
  const subscriptionField = get(selectedSubscription, fieldName);
  const parsedChargebackAmount = subscriptionField.match(moneyRegex);

  return parsedChargebackAmount ? parsedChargebackAmount[1] : 0;
};

export const getTicketFieldBasedOnPartner = ({
  partner,
  fieldName,
  ticketFields,
  selectedOrder,
  selectedSubscription,
}) => {
  let fieldValue;
  if (PARTNERS.RBI === partner) {
    fieldValue = get(selectedOrder, fieldName, undefined);
  } else if (PARTNERS.HUM === partner) {
    fieldValue = getSubscriptionField(selectedSubscription, fieldName);
  } else {
    fieldValue = get(ticketFields, fieldName, undefined);
  }

  return fieldValue;
};

export const convertToNumber = (value) => {
  const convertedNumber = toNumber(value);
  if (isNaN(convertedNumber) || isBoolean(value)) {
    throw new ConfigurableNodeError(GENERIC_ERROR_MESSAGE);
  }

  return convertedNumber;
};

export const removeLastCharacterFromString = (string) => string.substring(0, string.length - 1);

export function parseDateFromDescription(description) {
  // This regex captures dates in the format 'Mon DD - Mon DD' (e.g., 'Jan 01 - Jan 07')
  // The first group captures the start date (e.g., 'Jan 01') and the second group captures the end date (e.g., 'Jan 07')
  const dateRegex = /(\w{3} \d{1,2}) - (\w{3} \d{1,2})/;

  const dateMatches = description.match(dateRegex);

  if (dateMatches && dateMatches.length === 3) {
    const startDateStr = `${dateMatches[1]}, ${new Date().getFullYear()}`;
    const endDateStr = `${dateMatches[2]}, ${new Date().getFullYear()}`;

    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
      return { startDate, endDate };
    }
  }

  return { startDate: null, endDate: null };
}
